/**
 * Der zweite Finger (im Spiel aufgefallen am 27.09.2026).
 *
 * Das Problem: Auf dem Handy erzeugt der Browser nur fuer den ERSTEN Finger
 * ein click-Ereignis. Wer mit dem linken Daumen laeuft und mit dem rechten
 * einen Knopf antippt, loest deshalb gar nichts aus - der Knopf scheint
 * kaputt. Erst fiel das bei ARMY und DEFENSE auf, dann beim Schliessen eines
 * offenen Fensters.
 *
 * Erst habe ich einzelne Knoepfe umgestellt (siehe useTap in ui/usePress.ts).
 * Das half genau diesen Knoepfen - und dem naechsten, den jemand baut, eben
 * nicht. Deshalb steht die Regel jetzt an EINER Stelle: Bleibt fuer einen
 * zweiten Finger das click aus, wird es nachgereicht.
 *
 * Nachgereicht wird nur, wenn der Finger auf demselben Knopf aufgesetzt und
 * wieder losgelassen wurde. Wer danebengreift und wegzieht, loest nichts aus -
 * genau wie bei einem gewoehnlichen Knopf.
 */

/** Auf diese Elemente wird ein click nachgereicht. */
const KLICKBAR = 'button, [role="button"]';

/**
 * Laesst der Browser fuer diesen Zeiger das click aus?
 *
 * Genau dann, wenn es ein Finger ist und NICHT der erste: Fuer den ersten
 * Finger und fuer Maus oder Stift kommt das click von selbst - dort wuerde ein
 * zweites click den Knopf doppelt ausloesen.
 */
export function fehltDasClick(zeiger: { pointerType: string; isPrimary: boolean }): boolean {
  return zeiger.pointerType === 'touch' && !zeiger.isPrimary;
}

/** Der Knopf unter diesem Ziel - oder null, wenn dort keiner ist. */
export function findeKnopf(ziel: EventTarget | null): HTMLElement | null {
  if (!(ziel instanceof Element)) return null;
  const knopf = ziel.closest(KLICKBAR);
  if (!(knopf instanceof HTMLElement)) return null;
  // Ein ausgegrauter Knopf soll auch mit zwei Fingern nichts tun.
  if (knopf instanceof HTMLButtonElement && knopf.disabled) return null;
  if (knopf.getAttribute('aria-disabled') === 'true') return null;
  return knopf;
}

/**
 * Haengt die Regel ans Fenster und gibt zurueck, wie man sie wieder loest.
 *
 * Bewusst in der Blasenphase: So laeuft sie NACH den Knoepfen selbst. Knoepfe,
 * die schon auf pointerup hoeren, haben dann bereits ausgeloest und erkennen
 * das nachgereichte click als Nachhall (siehe useTap).
 */
export function installiereZweitenFinger(ziel: Window = window): () => void {
  /** Wo welcher Finger aufgesetzt wurde. */
  const start = new Map<number, HTMLElement>();

  const aufsetzen = (event: PointerEvent) => {
    if (!fehltDasClick(event)) return;
    const knopf = findeKnopf(event.target);
    if (knopf) start.set(event.pointerId, knopf);
  };

  const loslassen = (event: PointerEvent) => {
    const begonnen = start.get(event.pointerId);
    start.delete(event.pointerId);
    if (!begonnen || !fehltDasClick(event)) return;

    // Nur wenn der Finger auf demselben Knopf geblieben ist.
    const jetzt = findeKnopf(event.target);
    if (jetzt !== begonnen) return;

    begonnen.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true, view: ziel }),
    );
  };

  const abbrechen = (event: PointerEvent) => {
    start.delete(event.pointerId);
  };

  ziel.addEventListener('pointerdown', aufsetzen);
  ziel.addEventListener('pointerup', loslassen);
  ziel.addEventListener('pointercancel', abbrechen);

  return () => {
    ziel.removeEventListener('pointerdown', aufsetzen);
    ziel.removeEventListener('pointerup', loslassen);
    ziel.removeEventListener('pointercancel', abbrechen);
    start.clear();
  };
}
